/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FileFilterColumns,
  FileFilterModel,
  useFileFilter,
  useSetFileFilter,
  useValidateFileListOrder,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';

type FileFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const FileFilter = (props: FileFilterProps) => {
  const { data: fileFilter } = useFileFilter();
  const { mutateAsync: setFileFilter } = useSetFileFilter();
  const validateListOrder = useValidateFileListOrder();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: fileFilter?.columns,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: fileFilter?.columns,
    });
  }, [fileFilter, reset]);

  const selectedColumns = watch('columns');

  const isColumnsFilter = props.type === FilterType.FILECOLUMNS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new FileFilterModel().columns }),
    });
  }, [isColumnsFilter, reset, selectedColumns]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setFileFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new FileFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
    };

    resetForm();

    const mergedFilters = { ...fileFilter, ...updatedFields };
    await applyFilter(new FileFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new FileFilterModel({
      ...fileFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={FileFilterColumns}
        i18nPrefix="file.filter"
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
